/**
 * 이상한 방 - 세션 뷰 (상태 → 화면이 읽을 모양)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제) · 4.4절(정답 방)
 *
 * `session.ts`에서 갈라져 나왔다. 여기 있는 것은 전부 **인자만 보는 순수 함수**이고,
 * 하는 일은 `GameState`를 화면 모듈이 아는 모양으로 옮기는 **번역** 하나뿐이다.
 * 세션 클로저(씬·HUD 핸들·타이머)를 하나도 붙잡지 않으므로 렌더러 없이 그대로 부를 수 있다.
 *
 * ## 여기서 값을 새로 계산하지 않는다
 *
 * 배수·상한·거부 조건은 전부 규칙 모듈(`gameState` `combo` `hunt`)에서 그대로 옮겨 담는다.
 * 뷰가 하나라도 다시 계산하면 그 규칙이 즉시 두 벌이 되고, 튜닝 한 번에 **화면의 숫자와
 * 실제 동작이 갈린다** - 그 순간 계기판은 "눌리는데 아무 일도 없는" 물건이 된다.
 * 이 파일에 산술이 보이면 그건 규칙이 새어 나온 것이다.
 *
 * ## `ui/`의 타입을 가져오는 이유
 *
 * 세션은 접착층이라 `render`·`input`·`audio`·`ui`를 전부 부른다(CLAUDE.md의 의도된 예외).
 * 이 파일은 그 세션의 한 조각이므로 같은 예외 안에 있고, 실제로 가져오는 것은 **뷰 타입뿐**이다
 * (`import type`). 규칙 모듈이 `ui`를 부르기 시작하면 그건 연출이 섞인 것이지만,
 * 여기서 하는 일은 애초에 화면에 넘길 모양을 만드는 것이다.
 */

import type { GameHudView } from '../ui/gameHud'
import type { MissedChangeView } from '../ui/resultScreen'
import { comboMultiplier, isComboAtCap } from './combo'
import {
  canRaiseNow,
  canUseMagazine,
  changeSeedFor,
  currentCombo,
  hasRaised,
  isLowAmmo,
  magazineUsesLeft,
  phaseRemainingMs,
  type GameState,
} from './gameState'
import { originStatusOf as visitStatusOf, type OriginRoomStatus } from './originVisit'
import type { RevealStep } from './reveal'
import type { Tuning } from './tuning'

/**
 * 정답 방 버튼의 얼굴. 페이즈 게이트만 여기 것이고 방문 상태 → 얼굴은 리프(`originVisit.originStatusOf`)가 만든다 -
 * 매치 뷰(`match/matchView.ts`)도 같은 함수를 부르므로 게이지 분모·분기 순서가 두 벌이 되지 않는다.
 */
export function originStatusOf(state: GameState, tuning: Tuning): OriginRoomStatus {
  if (state.phase !== 'hunt') return { kind: 'unavailable' }
  return visitStatusOf(state, tuning)
}

/**
 * HUD 뷰. 외우기 페이즈에는 변화가 아직 없으므로(`changes.length === 0`) 목표 개수를 튜닝에서 읽는다 -
 * "0개 중 0개"라고 띄우면 이 라운드에 몇 개를 찾아야 하는지 알 수 없다.
 */
export function buildHudView(state: GameState, tuning: Tuning): GameHudView {
  return {
    phase: state.phase,
    phaseRemainingMs: phaseRemainingMs(state, tuning),
    // 혼자 하는 판에는 셀 표가 없다 - `needed: 1`이면 HUD가 세는 얼굴을 세우지 않는다(`GameHudView.skip`).
    skip: { votes: 0, needed: 1, mine: false },
    foundCount: state.found.length,
    changeCount: state.changes.length === 0 ? tuning.changeCount : state.changes.length,
    ammo: state.ammo,
    ammoMax: tuning.startAmmo,
    isLowAmmo: isLowAmmo(state, tuning),
    /*
     * 탄창 셋은 전부 규칙에서 그대로 옮긴다. 여기서 하나라도 다시 계산하면 버튼의 얼굴과
     * 실제 동작이 갈리고, 그때 화면은 "눌리는데 아무 일도 없는" 물건이 된다.
     * 세 값이 각각 다른 질문에 답한다 - 몇 개 있는가 / 이번 판에 몇 번 더 되는가 / 지금 되는가.
     */
    magazines: state.magazines,
    magazineUsesLeft: magazineUsesLeft(state, tuning),
    canUseMagazine: canUseMagazine(state, tuning),
    /*
     * 화면에 그리는 값이 아니라 **거부 사유를 숫자로 말하기 위한 것**이다 -
     * 이게 없으면 "탄창은 자리가 남아야 들어간다"까지만 말하고, 있으면
     * "6발 더 쓰면 탄창을 넣을 수 있다"가 된다(`gameHud.ts`의 `hud.mag.needRoomCount`).
     */
    ammoPerMagazine: tuning.ammoPerMagazine,
    isInOriginRoom: state.isInOriginRoom,
    origin: originStatusOf(state, tuning),
    /*
     * 콤보 셋도 전부 규칙에서 그대로 옮긴다. HUD가 콤보 수에서 배수를 다시 계산하면
     * 상한(3.0)이 두 곳에 생기고, 튜닝 한 번에 화면과 점수가 갈린다.
     */
    combo: currentCombo(state),
    comboMultiplier: comboMultiplier(currentCombo(state)),
    comboAtCap: isComboAtCap(currentCombo(state)),
    // 걸기(M7) 둘도 규칙에서 그대로 옮긴다. 조건(잔탄 · 1회)을 여기서 적으면 칩의 얼굴과 리듀서의 거부가 갈린다.
    raised: hasRaised(state),
    canRaise: canRaiseNow(state, tuning),
    // 싱글에는 역할이 하나다 - 걸기 칩은 언제나 내 것이고, 수색 중인가는 HUD가 `phase`로 본다.
    raiseVisible: true,
  }
}

/** 리빌 스텝 중 끝내 못 찾은 것만. 결과 화면의 "놓친 변화" 목록이며 라벨은 리빌과 **같은 문자열**이다. */
export function missedFrom(steps: readonly RevealStep[]): readonly MissedChangeView[] {
  return steps
    .filter((step) => !step.found)
    .map((step) => ({ id: step.change.id, verb: step.change.verb, label: step.label }))
}

/* --- 개발 훅이 읽는 모양 ------------------------------------------------------------ */

/**
 * `__weirdRoom.stats()`(`app/debugHooks.ts`)와 배치 HUD(`app/inputLayer.ts`)가 세션에게 묻는 숫자 전부.
 *
 * 싱글(`GameSession`)과 여럿이(`MatchSession`)가 **같은 모양**으로 답한다 - 무대(`app/stage.ts`)가 두 세션 중
 * 무엇을 세웠는지 개발 훅이 묻지 않게 하기 위해서다(`app/stageSession.ts`의 공통 표면). 여럿이에는 시도 번호가
 * 없어(`attempt`·`changeSeed`) 그 둘만 `null`을 허용한다. 뜻은 각 세션의 상태 필드 그대로이고 여기서 계산하지 않는다.
 */
export type SessionDebugInfo = Readonly<{
  kind: 'solo' | 'match'
  phase: string
  seed: string
  /** 싱글: 시도 번호 · 변화 시드(`?seed=…&attempt=N`이 재현의 두 절반). 여럿이: `null`. */
  attempt: number | null
  changeSeed: string | null
  ammo: number
  ammoMax: number
  magazines: number
  /** 이번 라운드에 쓴 탄창 수. 여럿이 뷰에는 없어 `null`(호스트는 "몇 번 더 되는가"만 내린다 - 지어내지 않는다). */
  magazinesUsed: number | null
  found: number
  changes: number
  isInOriginRoom: boolean
  score: number
}>

/** 싱글 세션의 개발 훅 모양. 이름 옮기기다 - 값은 전부 상태와 튜닝에서 그대로 온다. */
export function buildDebugInfo(state: GameState, tuning: Tuning): SessionDebugInfo {
  return {
    kind: 'solo',
    phase: state.phase,
    seed: state.seed,
    attempt: state.attempt,
    changeSeed: changeSeedFor(state),
    ammo: state.ammo,
    ammoMax: tuning.startAmmo,
    magazines: state.magazines,
    magazinesUsed: state.magazinesUsed,
    found: state.found.length,
    changes: state.changes.length,
    isInOriginRoom: state.isInOriginRoom,
    score: state.score,
  }
}
